import { supabase, isSupabaseConfigured } from "./supabase";

const BUCKET = "books";

// Book rows already use the same field names the UI reads (author_name,
// cover_image_url, pdf_url, intro_body, etc.) — no camelCase mapping layer
// needed here, unlike propertiesApi.js's rowToProperty/propertyToRow.

export async function fetchBookBySlug(slug) {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error("Supabase not configured") };
  }
  const { data, error } = await supabase
    .from("books")
    .select("*")
    .eq("slug", slug)
    .eq("published", true)
    .maybeSingle();
  return { data, error };
}

// Admin needs to see drafts too, not just published books.
export async function fetchBookById(id) {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error("Supabase not configured") };
  }
  const { data, error } = await supabase
    .from("books")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  return { data, error };
}

export async function fetchLatestPublishedBook() {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error("Supabase not configured") };
  }
  const { data, error } = await supabase
    .from("books")
    .select("*")
    .eq("published", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return { data, error };
}

// All published books, for the public /books catalog — used when there's
// more than one, so visitors get to pick instead of only ever seeing
// whichever one was created most recently (see fetchLatestPublishedBook).
export async function fetchPublishedBooks() {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error("Supabase not configured") };
  }
  const { data, error } = await supabase
    .from("books")
    .select("*")
    .eq("published", true)
    .order("created_at", { ascending: false });
  return { data, error };
}

export async function fetchAllBooks() {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error("Supabase not configured") };
  }
  const { data, error } = await supabase
    .from("books")
    .select("*")
    .order("created_at", { ascending: false });
  return { data, error };
}

function slugify(raw) {
  return String(raw)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

async function uploadBookFile(slug, prefix, file) {
  const path = `${slug}/${prefix}-${Date.now()}-${file.name}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type || undefined,
  });
  if (error) throw error;
  return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

/**
 * Creates a new book. `fields` is the plain book row (title, headline,
 * benefits, etc. — see sql/schema.sql for the full shape). `files` is
 * { pdf, cover, preview } — File objects or null/undefined.
 *
 * Runs entirely client-side against the anon key; the "admins can manage
 * books" RLS policy (auth.role() = 'authenticated') is what actually
 * allows this insert, not a service-role bypass.
 */
export async function createBook(fields, files = {}) {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error("Supabase not configured") };
  }
  const slug = slugify(fields.slug);
  if (!fields.title || !slug || !files.pdf) {
    return {
      data: null,
      error: new Error("Title, slug, and a PDF file are required."),
    };
  }

  try {
    const pdf_url = await uploadBookFile(slug, "pdf", files.pdf);
    const cover_image_url = files.cover
      ? await uploadBookFile(slug, "cover", files.cover)
      : null;
    const preview_image_url = files.preview
      ? await uploadBookFile(slug, "preview", files.preview)
      : null;

    const { data, error } = await supabase
      .from("books")
      .insert({ ...fields, slug, pdf_url, cover_image_url, preview_image_url })
      .select()
      .single();

    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Updates an existing book. Only replaces a file in Storage if a new one
 * was actually chosen — otherwise keeps whatever URL is already on the
 * row (passed in via `existing`).
 */
export async function updateBook(id, fields, files = {}, existing = {}) {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error("Supabase not configured") };
  }

  try {
    // The slug field is editable — admins were asked for this despite the
    // tradeoff (changing it breaks any link already shared with the old
    // slug). Newly uploaded files use the new slug as their storage
    // prefix; files kept from before stay under the old slug's folder,
    // which is harmless since their URLs don't change.
    const slug = fields.slug ? slugify(fields.slug) : existing.slug;
    const pdf_url = files.pdf
      ? await uploadBookFile(slug, "pdf", files.pdf)
      : existing.pdf_url;
    const cover_image_url = files.cover
      ? await uploadBookFile(slug, "cover", files.cover)
      : existing.cover_image_url;
    const preview_image_url = files.preview
      ? await uploadBookFile(slug, "preview", files.preview)
      : existing.preview_image_url;

    const { data, error } = await supabase
      .from("books")
      .update({ ...fields, slug, pdf_url, cover_image_url, preview_image_url })
      .eq("id", id)
      .select()
      .single();

    return { data, error };
  } catch (error) {
    return { data: null, error };
  }
}

/**
 * Toggles a book between published/draft without opening the full edit
 * form — used by the "Publish"/"Unpublish" button in the admin list.
 */
export async function setBookPublished(id, published) {
  if (!isSupabaseConfigured()) {
    return { error: new Error("Supabase not configured") };
  }
  const { error } = await supabase
    .from("books")
    .update({ published })
    .eq("id", id);
  return { error };
}

// Storage URLs are always .../object/public/books/<path> — this recovers
// <path> so it can be passed to storage.remove().
function storagePathFromPublicUrl(url) {
  if (!url) return null;
  const marker = `/object/public/${BUCKET}/`;
  const idx = url.indexOf(marker);
  return idx === -1 ? null : url.slice(idx + marker.length);
}

/**
 * Deletes a book row and best-effort removes its uploaded files from
 * Storage. The row delete is covered by the "admins can manage books" RLS
 * policy (for all operations); the Storage removal additionally needs a
 * delete policy on storage.objects (see sql/schema.sql) — without one it
 * silently deletes nothing, but the row itself is still removed either way.
 */
export async function deleteBook(book) {
  if (!isSupabaseConfigured()) {
    return { error: new Error("Supabase not configured") };
  }

  const paths = [book.pdf_url, book.cover_image_url, book.preview_image_url]
    .map(storagePathFromPublicUrl)
    .filter(Boolean);

  if (paths.length) {
    await supabase.storage.from(BUCKET).remove(paths);
  }

  const { error } = await supabase.from("books").delete().eq("id", book.id);
  return { error };
}

export { slugify };
